import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useUploadsInFlight } from '@/hooks/useUploadsInFlight';
import { Stepper } from '@/components/ui/Stepper';
import {
  createStartupResolver,
  STEP_FIELDS,
  type CreateStartupFormValues,
  type FoundingTeamMemberFormValues,
} from '../schema';
import { toCreatePayload } from '../to-payload';
import { useCreateStartup } from '../hooks/useCreateStartup';
import { useCreateStartupStore } from '../store/createStartupStore';
import {
  CREATE_STARTUP_STEPS,
  CREATE_STARTUP_STRINGS,
  INSTRUMENT_SEGMENT_OPTIONS,
} from '../constants';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { mapApiError } from '@/lib/utils/error.utils';
import { useAuth } from '@/hooks/useAuth';
import { Step1CompanyInfo } from './steps/Step1CompanyInfo';
import { Step2FoundingTeam } from './steps/Step2FoundingTeam';
import { Step3CurrentRaise } from './steps/Step3CurrentRaise';
import { Step4Review } from './steps/Step4Review';

const LAST_STEP = CREATE_STARTUP_STEPS.length - 1;

const SAVE_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.STARTUP_DOMAIN_ALREADY_REGISTERED]:
    CREATE_STARTUP_STRINGS.DOMAIN_ALREADY_REGISTERED_ERROR,
};

// pastExits deliberately omitted (not just ''): it's a Yes/No select with no
// sensible pre-filled default - it's optional to submit (2026-08-05, see
// schema.ts), but still shouldn't default to a value the user never chose.
// RHF genuinely supports an incomplete default (same reason DEFAULT_VALUES
// below is Partial<CreateStartupFormValues>) - the cast just satisfies the
// array element's stricter static type.
const EMPTY_MEMBER = {
  name: '',
  title: '',
  linkedinUrl: '',
} as FoundingTeamMemberFormValues;

// Stable reference for the "no draft yet" fallback - a fresh `{}` literal
// inside the store selector below would make Zustand's snapshot look
// different on every read even when the underlying draft hasn't changed,
// which forces React into an infinite re-render loop.
const EMPTY_VALUES: Partial<CreateStartupFormValues> = {};

const DEFAULT_VALUES: Partial<CreateStartupFormValues> = {
  companyName: '',
  marketModels: [],
  // One co-founder row is present by default (design decision 2026-07-15).
  foundingTeam: [EMPTY_MEMBER],
  // Figma defaults the instrument segmented control to SAFE (first option).
  instrument: INSTRUMENT_SEGMENT_OPTIONS[0].value,
};

export function CreateStartupForm() {
  // The account email doubles as the work email (user decision 2026-07-16),
  // required by the Backend's `assertWorkEmail`/`checkEligibility` domain
  // check on create - unrelated to OTP, which has been removed (Task 9).
  const { user } = useAuth();
  const workEmail = user?.email ?? '';
  // The wizard draft is keyed by user id (createStartupStore) so a
  // shared/kiosk machine never shows one user's in-progress draft to a
  // different user who logs in afterward.
  const userId = user?.id ?? '';
  const step = useCreateStartupStore((state) => state.drafts[userId]?.step ?? 0);
  const persistedValues = useCreateStartupStore(
    (state) => state.drafts[userId]?.values ?? EMPTY_VALUES,
  );
  const setStepForUser = useCreateStartupStore((state) => state.setStep);
  const setValuesForUser = useCreateStartupStore((state) => state.setValues);
  const resetDraftForUser = useCreateStartupStore((state) => state.reset);
  const setStep = (next: number) => setStepForUser(userId, next);

  const form = useForm<CreateStartupFormValues>({
    resolver: createStartupResolver,
    defaultValues: { ...DEFAULT_VALUES, ...persistedValues },
    mode: 'onTouched',
  });

  useEffect(() => {
    // RHF's watch callback yields a DeepPartial snapshot (nested object/array
    // fields are themselves partial); the store persists whatever was typed
    // so far, so a cast to the form's own Partial shape is safe here - full
    // validation still runs via zodResolver on submit regardless of what's
    // been persisted mid-wizard.
    const subscription = form.watch((values) =>
      setValuesForUser(userId, values as Partial<CreateStartupFormValues>),
    );
    return () => subscription.unsubscribe();
  }, [form, userId, setValuesForUser]);

  const create = useCreateStartup();
  const uploads = useUploadsInFlight();

  const goNext = async () => {
    const valid = await form.trigger(STEP_FIELDS[step] ?? []);
    if (valid) setStep(Math.min(step + 1, LAST_STEP));
  };

  const onSubmit = form.handleSubmit((values) =>
    create.mutate(toCreatePayload(values, workEmail), {
      onSuccess: () => resetDraftForUser(userId),
    }),
  );

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6">
      {/* The stepper keeps the shared 760px column on every step; Step 1's
          cards use the wider Figma frame (1312px), steps 2-4 stay at 760. */}
      <div className="mx-auto w-full max-w-190">
        <Stepper steps={[...CREATE_STARTUP_STEPS]} currentIndex={step} />
      </div>

      <div className={`flex flex-col gap-6 ${step === 0 ? '' : 'mx-auto w-full max-w-190'}`.trim()}>
        {step === 0 && <Step1CompanyInfo form={form} trackUpload={uploads.track} />}
        {step === 1 && <Step2FoundingTeam form={form} />}
        {step === 2 && <Step3CurrentRaise form={form} />}
        {step === 3 && (
          <Step4Review form={form} onEdit={setStep} isSaving={create.isPending} />
        )}

        {create.isError && (
          <p role="alert" className="text-sm text-danger">
            {mapApiError(create.error, SAVE_ERROR_BY_CODE, CREATE_STARTUP_STRINGS.SAVE_ERROR)}
          </p>
        )}

        {/* Figma: Back sits right next to Next (right-aligned, light border);
            on the last step Back stands alone on the left (198:598). */}
        <div className={`flex items-center gap-4 ${step === LAST_STEP ? 'justify-start' : 'justify-end'}`}>
        {step > 0 && (
          <Button
            type="button"
            variant="white"
            className="inline-flex items-center gap-1.5"
            onClick={() => setStep(Math.max(step - 1, 0))}
          >
            <ArrowLeft size={16} aria-hidden="true" />
            {CREATE_STARTUP_STRINGS.BACK}
          </Button>
        )}

        {step < LAST_STEP && (
          <Button
            type="button"
            variant="primary"
            className="inline-flex items-center gap-2"
            onClick={goNext}
            // Blocked mid-transfer: advancing past the upload step before the
            // key arrives loses the file silently, exactly as saving early does
            // on the profile forms.
            disabled={uploads.isUploading}
          >
            {CREATE_STARTUP_STRINGS.NEXT_PREFIX}
            {CREATE_STARTUP_STEPS[step + 1].label}
            <ArrowRight size={16} aria-hidden="true" />
          </Button>
        )}
        </div>
      </div>
    </form>
  );
}
